import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader, HpTag, TableScroll } from '@hp/ui';
import { trpc } from '../../api/trpc';
import { StatusBadge } from '../../components/badges';
import { ErrorAlert } from '../../components/error-alert';
import { RelativeTime } from '../../components/relative-time';
import { Spinner } from '../../components/ui/icons';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { fmtInt, progressOf } from '../../lib/format';
import { invalidatePerformance, useMediaStatus } from './queries';

/** Media spend per ad platform: what is connected, which days are covered, how much was spent, import controls. */
export function MediaPanel({ canRun }: { canRun: boolean }) {
  const { t } = useTranslation(['performance', 'common']);
  const status = useMediaStatus();
  const start = useMutation(trpc.performance.media.startImport.mutationOptions({ onSuccess: invalidatePerformance }));
  const active = status.data?.active ?? null;
  const progress = progressOf(active?.stats?.progress);
  const configured = status.data?.providers.filter((p) => p.configured).map((p) => p.provider) ?? [];
  const since = status.data?.since ?? '';

  return (
    <section className="hp-card">
      <HpCardHeader title={t('media.title')} subtitle={t('media.subtitle')} icon="megaphone">
        {active ? (
          <Spinner>
            {t('media.inProgress', { processed: fmtInt(progress?.processed ?? 0), of: progress?.of ?? '?' })}
          </Spinner>
        ) : (
          canRun &&
          configured.length > 0 && (
            <span className="bo-row">
              <HpButton
                variant="secondary"
                icon="refresh"
                className="bo-button--sm"
                busy={start.isPending && start.variables?.mode === 'refresh'}
                disabled={start.isPending}
                onClick={() => start.mutate({ providers: configured, mode: 'refresh' })}
              >
                {t('media.refresh')}
              </HpButton>
              <HpButton
                variant="secondary"
                icon="download"
                className="bo-button--sm"
                busy={start.isPending && start.variables?.mode === 'backfill'}
                disabled={start.isPending}
                onClick={() => start.mutate({ providers: configured, mode: 'backfill' })}
              >
                {t('media.backfill', { since })}
              </HpButton>
            </span>
          )
        )}
      </HpCardHeader>
      <div className="hp-card__body bo-sections">
        <ErrorAlert error={start.error ?? status.error} />
        <TableScroll label={t('media.title')}>
          <BrutTable>
            <BrutTheadRow>
              <th>{t('media.provider')}</th>
              <th>{t('media.state')}</th>
              <th>{t('media.coverage')}</th>
              <th className="bo-num">{t('media.campaigns')}</th>
              <th className="bo-num">{t('media.spend')}</th>
              <th>{t('media.lastImport')}</th>
            </BrutTheadRow>
            <tbody>
              {(status.data?.providers ?? []).map((p) => (
                <tr key={p.provider}>
                  <td>
                    <span className="bo-strong">{t(`channel.${p.provider}`)}</span>
                  </td>
                  <td>
                    {p.configured ? (
                      <HpTag tone="success">{t('media.configured')}</HpTag>
                    ) : (
                      <HpTag tone="neutral">{t('media.notConfigured')}</HpTag>
                    )}
                  </td>
                  <td className="bo-mono">
                    {p.coverage?.firstDay ? `${p.coverage.firstDay} – ${p.coverage.lastDay ?? ''}` : '—'}
                  </td>
                  <td className="bo-num">{p.coverage ? fmtInt(p.coverage.campaigns) : '—'}</td>
                  <td className="bo-num">
                    {p.coverage ? fmtInt(Math.round(p.coverage.costCzk)) : '—'}
                    {p.coverage?.hasForeign && <small className="bo-muted"> {t('media.foreign')}</small>}
                  </td>
                  <td>
                    {p.lastRun ? (
                      <span className="bo-row">
                        <StatusBadge status={p.lastRun.status} />
                        <RelativeTime value={p.lastRun.finishedAt ?? p.lastRun.createdAt} />
                      </span>
                    ) : (
                      <span className="bo-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </BrutTable>
        </TableScroll>
        {status.data && (
          <p className="bo-muted">
            {status.data.fx.days > 0
              ? t('media.fx', {
                  first: status.data.fx.firstDay ?? '',
                  last: status.data.fx.lastDay ?? '',
                  currencies: status.data.fx.currencies,
                })
              : t('media.fxNone')}
          </p>
        )}
        <p className="bo-hint">{t('media.hint')}</p>
      </div>
    </section>
  );
}
